<template>
  <div class="player-performance-review-stats">
    <h3>Physical Performance</h3>

    <a href="javascript:;" @click="showPhysicalPerformanceStatsModal = true" class="view-all-reports">
      <img class="large-icon-shape" src="./img/performance-stats-icon.svg" alt="" />
      <span>View physical <br />performance reports</span>
    </a>
  </div>

  <div class="player-performance-review-info">
    <b-modal v-if="showPhysicalPerformanceStatsModal" v-model="showPhysicalPerformanceStatsModal"
      modal-class="modal-fullscreen" size="lg" hide-footer>
      <div class="physical-performance-wrapper">
        <!-- Timeline & Metric filters -->
        <div class="pp-filter-bar">
          <b-form-group label="Timeline" label-for="timeline-select" class="pp-filter">
            <b-form-select id="timeline-select" v-model="selectedTimeline" :options="timelineOptions" size="sm" />
          </b-form-group>
          <b-form-group label="Metric" label-for="metric-select" class="pp-filter">
            <b-form-select id="metric-select" v-model="selectedMetric" :options="metricOptions" size="sm" />
          </b-form-group>
          <b-form-group v-if="selectedTimeline === 'custom'" label="Date range" class="pp-filter pp-filter-date">
            <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" v-model="customDateRange" range />
          </b-form-group>
        </div>

        <div class="physical-performance-stats" :class="{ 'last-season-theme': showLastSeasonView }">
          <div class="season-header" style="
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 0.5rem;
            ">
            <h3 style="margin: 0">Physical Performance</h3>
            <span class="season-label badge badge-info" style="font-size: 0.75rem">
              {{
                (timelineOptions &&
                  selectedTimeline &&
                  timelineOptions.find((opt) => opt.value === selectedTimeline)
                    ?.text) ||
                "Timeline"
              }}
            </span>
          </div>

          <p class="small-date-player-coach">
            Last reviewed
            {{ formatDateForHuman(getCurrentSeasonPerformanceData().date) }}
          </p>

          <!-- Performance Insights Section -->
          <div v-if="getPerformanceInsights().length > 0" class="performance-insights-section">
            <h5>Recent Performance Insights</h5>
            <div class="insights-grid">
              <div v-for="insight in getPerformanceInsights()" :key="insight.metric" class="insight-card"
                :class="insight.trend">
                <div class="insight-header">
                  <span class="insight-metric">{{ insight.metric }}</span>
                  <span class="insight-trend-icon">
                    <i v-if="insight.trend === 'improving'" class="bi bi-trending-up text-success"></i>
                    <i v-else-if="insight.trend === 'declining'" class="bi bi-trending-down text-warning"></i>
                    <i v-else class="bi bi-dash-circle text-muted"></i>
                  </span>
                </div>
                <p class="insight-message">{{ insight.message }}</p>
              </div>
            </div>
          </div>
          <div class="pp-latest" v-if="latestSession && latestTiles.length">
            <p class="pp-latest-date">
              Last tested {{ formatDateForHuman(latestSession.date) }}
            </p>
            <div class="pp-tile-grid">
              <div class="pp-tile" v-for="t in latestTiles" :key="t.key">
                <div class="pp-tile-label">{{ t.label }}</div>
                <div class="pp-tile-value">
                  <span class="pp-tile-number">{{ t.value }}</span>
                  <span class="pp-tile-unit" v-if="t.unit">{{ t.unit }}</span>
                  <i v-if="t.icon === 'better'" class="bi bi-arrow-up-circle-fill pp-arrow better"
                    title="Improved on the previous test"></i>
                  <i v-else-if="t.icon === 'worse'" class="bi bi-arrow-down-circle-fill pp-arrow worse"
                    title="Down on the previous test"></i>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="pp-empty">No physical performance data yet.</div>

          <div class="buttons-wrapper">
            <div class="pp-actions-row">
              <b-button v-if="isStaffViewer" size="sm" variant="success"
                @click="addNewPhysicalPerformanceTimeModal()">
                <i class="bi bi-plus-lg" aria-hidden="true"></i> Add
              </b-button>

              <b-button size="sm" variant="outline-secondary" @click="openPhysicalPerformanceHistoryModal()">
                <i class="bi bi-clock-history" aria-hidden="true"></i> History
              </b-button>

              <b-button size="sm" variant="outline-secondary" @click="
                download_csv(
                  allPlayerPhysicalPerformanceData,
                  'player-Physical-Performance-data',
                )
                ">
                <i class="bi bi-download" aria-hidden="true"></i> Export
              </b-button>
            </div>
          </div>
        </div>
        <div class="physical-performance-trends" v-if="getSeasonPerformanceDataForTrends().length > 0">
          <!-- Enhanced Physical Performance Trends Component -->
          <div class="trends-header" style="
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 1rem;
            ">
            <h4 style="margin: 0">Performance Trends</h4>
            <span class="season-label badge" :class="showLastSeasonView ? 'badge-secondary' : 'badge-success'"
              style="font-size: 0.75rem">
              {{
                showLastSeasonView
                  ? "Last Season Trends"
                  : "Current Season Trends"
              }}
            </span>
          </div>
          <PhysicalPerformanceTrendsSimple :performance-data="getSeasonPerformanceDataForTrends()"
            :can-see-weight="canSeeWeightTrend" />
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showPhysicalPerformanceHistoryModal" v-model="showPhysicalPerformanceHistoryModal"
      modal-class="modal-fullscreen" size="lg" hide-footer>
      <div class="physical-performance-table">
        <table class="w-a-style physicalPerformanceTable">
          <thead>
            <tr>
              <th>Date</th>
              <th>10m SPRINT</th>
              <th>30m SPRINT</th>
              <th>Countermovement jump</th>
              <th>Fitness Test Time</th>
              <th v-if="isStaffViewer">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="session in allPlayerPhysicalPerformanceData" :key="session.id">
              <td>{{ formatDateForHuman(session.date) }}</td>
              <td>{{ session.tenM_sprint_time }}</td>
              <td>{{ session.fortyM_sprint_time }}</td>
              <td>{{ session.cuntermovement_jump }}</td>
              <td>{{ session.thirty_15_intermittent_fitness_test }}</td>
              <td v-if="isStaffViewer" class="pp-actions">
                <b-button size="sm" variant="outline-secondary" v-b-tooltip.hover title="Edit record"
                  @click="editPhysicalPerformanceRecord(session)">
                  <i class="bi bi-pencil" aria-hidden="true"></i>
                </b-button>
                <b-button size="sm" variant="outline-danger" v-b-tooltip.hover title="Delete record"
                  @click="deletePhysicalPerformanceRecord(session)">
                  <i class="bi bi-trash" aria-hidden="true"></i>
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>

    <b-modal v-if="showAddNewPhysicalPerformanceTimeModal" v-model="showAddNewPhysicalPerformanceTimeModal" size="lg"
      hide-footer>
      <h2>{{ editingId ? "Edit" : "Add" }} Physical Performance</h2>

      <form class="form" @submit.prevent="addPhysicalPerformanceValue()">
        <div class="form__item">
          <label class="form__label" for="performance-date">Date:</label>
          <VueDatePicker
            id="performance-date"
            :locale="'en-GB'"
            v-model="playerPhysicalPerformanceData.date"
            :enable-time-picker="false"
            :max-date="new Date()"
            :clearable="false"
            auto-apply
            format="dd/MM/yyyy"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="pp-10m">10m SPRINT (s):</label>
          <input class="form__input" type="number" step=".01" min="1" max="6" id="pp-10m"
            v-model="playerPhysicalPerformanceData.tenM_sprint_time" />
        </div>

        <div class="form__item">
          <label class="form__label" for="pp-30m">30m SPRINT (s):</label>
          <input class="form__input" type="number" step=".01" min="2" max="12" id="pp-30m"
            v-model="playerPhysicalPerformanceData.fortyM_sprint_time" />
        </div>

        <div class="form__item">
          <label class="form__label" for="pp-cmj">Countermovement jump (cm):</label>
          <input class="form__input" type="number" step=".01" min="5" max="90" id="pp-cmj"
            v-model="playerPhysicalPerformanceData.cuntermovement_jump" />
        </div>

        <div class="form__item">
          <label class="form__label" for="pp-rsi">Reactive strength index:</label>
          <input class="form__input" type="number" step=".01" min="0.1" max="6" id="pp-rsi"
            v-model="playerPhysicalPerformanceData.reactive_strength_index" />
        </div>

        <div class="form__item">
          <label class="form__label" for="pp-fitness">Fitness Test Time:</label>
          <input class="form__input" type="number" step=".01" min="5" max="35" id="pp-fitness"
            v-model="playerPhysicalPerformanceData.thirty_15_intermittent_fitness_test" />
        </div>

        <div class="form__item">
          <label class="form__label" for="height">Height (cm):</label>
          <input class="form__input" type="number" step=".01" min="100" max="230" id="height"
            v-model="playerPhysicalPerformanceData.height" />
        </div>

        <div class="form__item">
          <label class="form__label" for="weight">Weight (kg):</label>
          <input class="form__input" type="number" step=".01" min="20" max="150" id="weight"
            v-model="playerPhysicalPerformanceData.weight" />
        </div>

        <b-button type="submit" size="lg" variant="outline-success" :disabled="saving">
          {{ saving ? "Saving…" : editingId ? "Save changes" : "Add entry" }}</b-button>
      </form>
    </b-modal>
  </div>
</template>
<script>
import { PlayerPhysicalPerformance } from "@/services/player-physical-performance-service.js";
import { getNutritionAccess } from "@/composables/useNutritionAccess";
import { METRICS, isValidValue } from "@/utils/physicalPerformanceMetrics";
import PhysicalPerformanceTrendsSimple from "../../components/performance/PhysicalPerformanceTrendsSimple";
import VueDatePicker from "@vuepic/vue-datepicker";
import moment from "moment";
export default {
  name: "playerPhysicalPerformanceStats",
  components: {
    VueDatePicker,
    PhysicalPerformanceTrendsSimple,
  },
  data() {
    return {
      selectedTimeline: "all",
      timelineOptions: [
        { value: "all", text: "All Time" },
        { value: "custom", text: "Custom Range" },
      ],
      selectedMetric: "all",
      metricOptionsBase: [
        { value: "all", text: "All Metrics" },
        { value: "cuntermovement_jump", text: "Countermovement Jump (cm)" },
        { value: "tenM_sprint_time", text: "10m SPRINT" },
        { value: "fortyM_sprint_time", text: "30m SPRINT" },
        { value: "reactive_strength_index", text: "Reactive strength index" },
        {
          value: "thirty_15_intermittent_fitness_test",
          text: "Fitness Test Time",
        },
        { value: "height", text: "Height" },
        { value: "weight", text: "Weight" },
      ],
      customDateRange: null,
      allPlayerPhysicalPerformanceData: [],
      playerPhysicalPerformanceData: {},
      showPhysicalPerformanceHistoryModal: false,
      showAddNewPhysicalPerformanceTimeModal: false,
      showPhysicalPerformanceStatsModal: false,
      currentSessionIndex: 0,
      editingId: null,
      saving: false,
      autoScrollInterval: null,
      touchStartX: 0,
      touchEndX: 0,
    };
  },
  props: {
    selectedUserValue: {
      type: Number,
      required: true,
    },
    userData: {
      type: Object,
      required: true,
    },
    // The Testing page is already role-gated by the router; let it enable
    // the add/edit controls without re-deriving staff-ness here.
    staffOverride: {
      type: Boolean,
      default: false,
    },
    selectedUserData: {
      type: Object,
      required: true,
    },
    currentSeason: {
      type: Object,
    },
    getLastSeason: {
      type: Object,
    },
    playerPerformanceReviewTargetsData: {
      type: Array,
    },
    performanceTargetsModal: {
      type: Function,
    },
    performanceTargetsHistoryModal: {
      type: Function,
    },
    showLastSeasonView: {
      type: Boolean,
    },
  },
  watch: {
    filteredPhysicalPerformanceData() {
      this.currentSessionIndex = 0;
    },
  },
  computed: {
    // Welfare guardrail: body-weight trends are staff-only (never any player,
    // even Performance phase). See useNutritionAccess.js.
    canSeeWeightTrend() {
      return getNutritionAccess(this.userData).canSeeWeightTrend;
    },
    // Staff (admin/coach) — may add/edit/delete records. The restricted
    // SportsScience role may too (it's the one thing it's for), but must not
    // inherit nutrition's staff view, so it's checked here rather than in
    // useNutritionAccess.
    isStaffViewer() {
      return (
        this.staffOverride ||
        this.userData?.userType === "SportsScience" ||
        getNutritionAccess(this.userData).isStaff
      );
    },
    // Hide the Weight metric (selector, tiles, trend chart) from non-staff.
    metricOptions() {
      if (this.canSeeWeightTrend) return this.metricOptionsBase;
      return this.metricOptionsBase.filter((m) => m.value !== "weight");
    },
    // Most recent test in the current view (by date).
    latestSession() {
      const arr = this.filteredPhysicalPerformanceData || [];
      if (!arr.length) return null;
      return arr.reduce((a, b) =>
        new Date(b.date) > new Date(a.date) ? b : a,
      );
    },
    // The latest session's measured metrics, as tiles (respects the metric
    // filter and the weight gate; skips unmeasured values).
    latestTiles() {
      const s = this.latestSession;
      if (!s) return [];
      return this.metricOptions
        .filter((m) => m.value !== "all")
        .filter(
          (m) =>
            this.selectedMetric === "all" || m.value === this.selectedMetric,
        )
        .filter((m) => isValidValue(s[m.value]))
        .map((m) => ({
          key: m.value,
          label: m.text,
          value: s[m.value],
          unit: (METRICS[m.value] && METRICS[m.value].unit) || "",
          icon: s[m.value + "_icon"] || null,
        }));
    },
    filteredPhysicalPerformanceData() {
      console.log("Selected selectedMetric:", this.selectedMetric);
      let data = this.allPlayerPhysicalPerformanceData || [];

      // Timeline filtering
      if (
        this.selectedTimeline === "custom" &&
        this.customDateRange &&
        Array.isArray(this.customDateRange) &&
        this.customDateRange.length === 2
      ) {
        const [start, end] = this.customDateRange;
        data = data.filter((session) => {
          const sessionDate = new Date(session.date);
          return sessionDate >= new Date(start) && sessionDate <= new Date(end);
        });
      }
      // Metric filtering
      if (this.selectedMetric && this.selectedMetric === "thisSeason") {
        const start = this.getCurrentSeason().startDate;
        const end = this.getCurrentSeason().endDate;
        data = data.filter((session) => {
          const sessionDate = new Date(session.date);
          return sessionDate >= new Date(start) && sessionDate <= new Date(end);
        });
      }

      if (this.selectedMetric && this.selectedMetric === "lastSeason") {
        const start = this.getLastSeason().startDate;
        const end = this.getLastSeason().endDate;
        data = data.filter((session) => {
          const sessionDate = new Date(session.date);
          return sessionDate >= new Date(start) && sessionDate <= new Date(end);
        });
      }

      console.log("Filtering physical performance data:", data);

      return data;
    },
  },
  async created() {
    await this.loadPlayerPhysicalPerformanceData();
  },
  beforeUnmount() {
    this.stopAutoScroll();
  },
  methods: {
    nextSession() {
      if (
        this.currentSessionIndex <
        this.filteredPhysicalPerformanceData.length - 1
      ) {
        this.currentSessionIndex++;
      }
    },
    prevSession() {
      if (this.currentSessionIndex > 0) {
        this.currentSessionIndex--;
      }
    },
    startAutoScroll() {
      this.stopAutoScroll();
      this.autoScrollInterval = setInterval(() => {
        if (
          this.currentSessionIndex <
          this.filteredPhysicalPerformanceData.length - 1
        ) {
          this.currentSessionIndex++;
        } else {
          this.currentSessionIndex = 0;
        }
      }, 5000); // Change session every 5 seconds
    },
    stopAutoScroll() {
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
        this.autoScrollInterval = null;
      }
    },
    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    },
    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      if (this.touchEndX < this.touchStartX - 30) {
        this.nextSession();
        this.startAutoScroll();
      } else if (this.touchEndX > this.touchStartX + 30) {
        this.prevSession();
        this.startAutoScroll();
      }
    },
    getSeasonPerformanceDataForTrends() {
      return this.allPlayerPhysicalPerformanceData || [];
    },
    validatePhysicalPerformance() {
      const data = this.playerPhysicalPerformanceData;
      const errors = [];
      if (!data.date) errors.push("Please select a date.");
      let anyValue = false;
      Object.keys(METRICS).forEach((field) => {
        const raw = data[field];
        if (raw === "" || raw === null || raw === undefined) return; // not measured
        anyValue = true;
        const n = Number(raw);
        const { min, max, label } = METRICS[field];
        if (isNaN(n)) {
          errors.push(`${label} must be a number.`);
        } else if (n < min || n > max) {
          errors.push(`${label} looks off — expected ${min}–${max}.`);
        }
      });
      if (!anyValue) errors.push("Enter at least one measurement.");
      return errors;
    },
    async addPhysicalPerformanceValue() {
      const validationErrors = this.validatePhysicalPerformance();
      if (validationErrors.length) {
        this.$toast.error(validationErrors.join(" "), {
          title: "Check the entry",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
        });
        return;
      }
      const selectedDate = this.playerPhysicalPerformanceData.date;
      const formattedDate =
        selectedDate && moment(selectedDate).isValid()
          ? moment(selectedDate).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD");
      this.playerPhysicalPerformanceData.date = formattedDate;
      const d = this.playerPhysicalPerformanceData;
      const isEdit = !!this.editingId;

      // Warn if a record already exists for this date (add only).
      if (
        !isEdit &&
        (this.allPlayerPhysicalPerformanceData || []).some(
          (r) => moment(r.date).format("YYYY-MM-DD") === formattedDate,
        ) &&
        !window.confirm(
          "A record already exists for this date. Add another anyway?",
        )
      ) {
        return;
      }

      this.saving = true;
      const userData = {
        date: formattedDate,
        player_id: this.selectedUserValue,
        tenM_sprint_time: d.tenM_sprint_time || 0,
        fortyM_sprint_time: d.fortyM_sprint_time || 0,
        cuntermovement_jump: d.cuntermovement_jump || 0,
        reactive_strength_index: d.reactive_strength_index || 0,
        thirty_15_intermittent_fitness_test: d.thirty_15_intermittent_fitness_test || 0,
        weight: d.weight || 0,
        height: d.height || 0,
        action: isEdit
          ? "updatePlayerPhysicalPerformanceData"
          : "addPlayerPhysicalPerformanceData",
      };
      if (isEdit) {
        userData.id = this.editingId;
        userData.updated_by = this.userData && this.userData.id;
      } else {
        userData.created_by = this.userData && this.userData.id;
      }

      const responce = await PlayerPhysicalPerformance.find(userData).catch(
        (error) => {
          console.log(error);
        },
      );

      const data = responce && responce.data;
      const ok =
        data === "New player physical performance results added." ||
        (data && data.success === true);
      if (ok) {
        await this.checkIfNewValueisBetterOrWorse();
        this.loadPlayerPhysicalPerformanceData();
        this.hideModal("showAddNewPhysicalPerformanceTimeModal");
        this.editingId = null;

        this.$toast.info(
          isEdit ? "Record updated." : "Player Physical Performance saved.",
          {
            title: "saved",
            toaster: "b-toaster-bottom-right",
            solid: true,
            appendToast: true,
            variant: "success",
          },
        );
      } else if (data && data.error) {
        this.$toast.error(data.error, {
          title: "Not saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
        });
      }
      this.saving = false;
    },

    editPhysicalPerformanceRecord(record) {
      this.playerPhysicalPerformanceData = { ...record };
      this.editingId = record.id;
      this.hideModal("showPhysicalPerformanceHistoryModal");
      this.showModal("showAddNewPhysicalPerformanceTimeModal");
    },

    async deletePhysicalPerformanceRecord(record) {
      if (!record || !record.id) return;
      const when = this.formatDateForHuman(record.date);
      if (!window.confirm(`Delete the physical performance record from ${when}? This cannot be undone.`)) {
        return;
      }
      const responce = await PlayerPhysicalPerformance.find({
        action: "deletePlayerPhysicalPerformanceData",
        id: record.id,
      }).catch((error) => console.log(error));
      if (responce && responce.data && responce.data.success) {
        this.loadPlayerPhysicalPerformanceData();
        this.$toast.info("Record deleted.", {
          title: "Deleted",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    hideModal(modelId) {
      this[modelId] = false;
    },
    showModal(modelId) {
      this[modelId] = true;
    },
    addNewPhysicalPerformanceTimeModal() {
      this.editingId = null;
      this.playerPhysicalPerformanceData = { date: new Date() };
      this.showModal("showAddNewPhysicalPerformanceTimeModal");
    },
    openPhysicalPerformanceHistoryModal() {
      this.showModal("showPhysicalPerformanceHistoryModal");
    },
    formatDateForHuman(date) {
      return date !== undefined && moment(date).isValid()
        ? moment(date).format("MMMM Do YYYY")
        : "";
    },
    getCurrentSeasonPerformanceData() {
      return this.playerPhysicalPerformanceData || {};
    },
    async loadPlayerPhysicalPerformanceData() {
      const userData = {
        player_id: this.selectedUserValue,
        action: "loadPlayerPhysicalPerformanceDataById",
      };
      const responce = await PlayerPhysicalPerformance.find(userData).catch(
        (error) => {
          console.log(error);
        },
      );

      console.log(responce.data, "player physical data");

      if (responce.data.length > 0) {
        this.allPlayerPhysicalPerformanceData = responce.data;
        this.playerPhysicalPerformanceData =
          this.getPlayerPhysicalPerformanceData();
        this.checkIfNewValueisBetterOrWorse();
        this.populatePhysicalPerformanceLineChartData();
      }
    },
    getPlayerPhysicalPerformanceData() {
      return this.allPlayerPhysicalPerformanceData.reduce((a, b) => {
        return new Date(a.date) > new Date(b.date) ? a : b;
      });
    },
    /**
     * Enhanced performance analysis that compares against rolling averages and trends
     * instead of just the last two data points for more meaningful improvement detection
     */
    checkIfNewValueisBetterOrWorse() {
      if (this.allPlayerPhysicalPerformanceData.length < 2) {
        return false;
      }

      const data = [...this.allPlayerPhysicalPerformanceData];
      const lastIndex = data.length - 1;
      const lastItem = { ...data[lastIndex] };

      // Define metric configurations
      const metricConfigs = [
        {
          field: "tenM_sprint_time",
          iconField: "tenM_sprint_time_icon",
          lowerIsBetter: true,
          threshold: 0.05,
        },
        {
          field: "tenM_sprint_time_1",
          iconField: "tenM_sprint_time_icon_1",
          lowerIsBetter: true,
          threshold: 0.05,
        },
        {
          field: "fortyM_sprint_time",
          iconField: "fortyM_sprint_time_icon",
          lowerIsBetter: true,
          threshold: 0.05,
        },
        {
          field: "fortyM_sprint_time_1",
          iconField: "fortyM_sprint_time_icon_1",
          lowerIsBetter: true,
          threshold: 0.05,
        },
        {
          field: "cuntermovement_jump",
          iconField: "cuntermovement_jump_icon",
          lowerIsBetter: false,
          threshold: 2,
        },
        {
          field: "cuntermovement_jump_2",
          iconField: "cuntermovement_jump_icon_2",
          lowerIsBetter: false,
          threshold: 2,
        },
        {
          field: "reactive_strength_index",
          iconField: "reactive_strength_index_icon",
          lowerIsBetter: false,
          threshold: 0.1,
        },
        {
          field: "reactive_strength_index_2",
          iconField: "reactive_strength_index_icon_2",
          lowerIsBetter: false,
          threshold: 0.1,
        },
        {
          field: "reactive_strength_index_3",
          iconField: "reactive_strength_index_icon_3",
          lowerIsBetter: false,
          threshold: 0.1,
        },
        {
          field: "reactive_strength_index_4",
          iconField: "reactive_strength_index_icon_4",
          lowerIsBetter: false,
          threshold: 0.1,
        },
        {
          field: "reactive_strength_index_5",
          iconField: "reactive_strength_index_icon_5",
          lowerIsBetter: false,
          threshold: 0.1,
        },
        {
          field: "reactive_strength_index_average",
          iconField: "reactive_strength_index_average_icon",
          lowerIsBetter: false,
          threshold: 0.1,
        },
        {
          field: "thirty_15_intermittent_fitness_test",
          iconField: "thirty_15_intermittent_fitness_test_icon",
          lowerIsBetter: true,
          threshold: 0.5,
        },
        {
          field: "weight",
          iconField: "weight_icon",
          lowerIsBetter: false,
          threshold: 1,
        },
        {
          field: "height",
          iconField: "height_icon",
          lowerIsBetter: false,
          threshold: 0.5,
        },
        {
          field: "sitting_height",
          iconField: "height_icon_sitting",
          lowerIsBetter: false,
          threshold: 0.5,
        },
        {
          field: "bronco_time_mins",
          iconField: "bronco_time_mins_icon",
          lowerIsBetter: true,
          threshold: 0.1,
        },
        {
          field: "bronco_time_sec",
          iconField: "bronco_time_sec_icon",
          lowerIsBetter: true,
          threshold: 1,
        },
      ];

      // Analyze each metric
      metricConfigs.forEach((config) => {
        const status = this.analyzeMetricTrend(
          data,
          config.field,
          config.lowerIsBetter,
          config.threshold,
        );
        lastItem[config.iconField] = status;
      });

      // Update the data array
      this.allPlayerPhysicalPerformanceData = [
        ...data.slice(0, lastIndex),
        lastItem,
      ];
    },

    /**
     * Analyzes a specific metric's trend using rolling averages and statistical thresholds
     */
    analyzeMetricTrend(data, metricField, lowerIsBetter, threshold) {
      console.log(`Analyzing trend for ${metricField}`);
      // Enhanced filtering to exclude invalid/empty data
      // isValidValue is shared from @/utils/physicalPerformanceMetrics

      // Filter out sessions with invalid data for this metric
      const validData = data.filter((session) =>
        isValidValue(session[metricField]),
      );

      if (validData.length < 2) return "same";

      // For fallback comparison, use valid data only
      if (validData.length < 3) {
        const lastValidValue = parseFloat(
          validData[validData.length - 1][metricField],
        );
        const previousValidValue = parseFloat(
          validData[validData.length - 2][metricField],
        );

        if (lowerIsBetter) {
          return lastValidValue < previousValidValue
            ? "better"
            : lastValidValue > previousValidValue
              ? "worse"
              : "same";
        } else {
          return lastValidValue > previousValidValue
            ? "better"
            : lastValidValue < previousValidValue
              ? "worse"
              : "same";
        }
      }

      const lastValidValue = parseFloat(
        validData[validData.length - 1][metricField],
      );

      // Calculate rolling average using only valid previous sessions
      const rollingWindowSize = Math.min(5, validData.length - 1);
      const previousValidSessions = validData.slice(-rollingWindowSize - 1, -1);

      if (previousValidSessions.length === 0) return "same";

      const averagePreviousValue =
        previousValidSessions.reduce(
          (sum, session) => sum + parseFloat(session[metricField]),
          0,
        ) / previousValidSessions.length;

      const percentageChange =
        Math.abs(
          (lastValidValue - averagePreviousValue) / averagePreviousValue,
        ) * 100;

      // Check if change is significant enough to be meaningful
      if (percentageChange < threshold) {
        return "same";
      }

      // Determine if this is an improvement based on metric type
      if (lowerIsBetter) {
        return lastValidValue < averagePreviousValue ? "better" : "worse";
      } else {
        return lastValidValue > averagePreviousValue ? "better" : "worse";
      }
    },

    /**
     * Get performance insights for the current player
     */
    getPerformanceInsights() {
      console.log("Analyzing key metrics for trends...");
      console.log(this.allPlayerPhysicalPerformanceData.length);
      if (this.allPlayerPhysicalPerformanceData.length < 3) {
        return [];
      }

      const insights = [];
      const recentSessions = this.allPlayerPhysicalPerformanceData.slice(-5);

      // Analyze key metrics for trends
      const keyMetrics = [
        {
          field: "tenM_sprint_time",
          name: "10m Sprint",
          unit: "s",
          lowerIsBetter: true,
        },
        {
          field: "fortyM_sprint_time",
          name: "30m Sprint",
          unit: "s",
          lowerIsBetter: true,
        },
        {
          field: "cuntermovement_jump",
          name: "Jump Height",
          unit: "cm",
          lowerIsBetter: false,
        },
        {
          field: "thirty_15_intermittent_fitness_test",
          name: "Fitness Test",
          unit: "",
          lowerIsBetter: true,
        },
      ];

      keyMetrics.forEach((metric) => {
        const trend = this.calculateMetricTrend(
          recentSessions,
          metric.field,
          metric.lowerIsBetter,
        );
        if (trend.status !== "stable") {
          insights.push({
            metric: metric.name,
            trend: trend.status,
            change: trend.change,
            unit: metric.unit,
            message: this.generateInsightMessage(
              metric.name,
              trend.status,
              trend.change,
              metric.unit,
            ),
          });
        }
      });

      return insights;
    },

    /**
     * Calculate trend for a specific metric over recent test sessions
     * Compares the most recent tests against previous baseline tests
     */
    calculateMetricTrend(sessions, metricField, lowerIsBetter) {
      // Enhanced filtering to exclude invalid/empty data
      // isValidValue is shared from @/utils/physicalPerformanceMetrics

      // Filter and sort sessions by date to ensure chronological order
      const validSessions = sessions
        .filter((session) => isValidValue(session[metricField]))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      if (validSessions.length < 3) return { status: "stable", change: 0 };

      // For performance tests, compare most recent tests vs older baseline
      // Take the last 30% of tests as "recent" and previous 30% as "baseline"
      const totalTests = validSessions.length;
      const recentTestsCount = Math.max(1, Math.floor(totalTests * 0.3));
      const baselineTestsCount = Math.max(1, Math.floor(totalTests * 0.3));

      // Ensure we have enough tests for meaningful comparison
      if (totalTests < recentTestsCount + baselineTestsCount) {
        return { status: "stable", change: 0 };
      }

      // Get most recent tests (latest performance data)
      const recentTests = validSessions.slice(-recentTestsCount);
      // Get baseline tests (older performance data, but not the very oldest)
      const baselineTests = validSessions.slice(
        -(recentTestsCount + baselineTestsCount),
        -recentTestsCount,
      );

      const recentAvg =
        recentTests.reduce(
          (sum, session) => sum + parseFloat(session[metricField]),
          0,
        ) / recentTests.length;
      const baselineAvg =
        baselineTests.reduce(
          (sum, session) => sum + parseFloat(session[metricField]),
          0,
        ) / baselineTests.length;

      // Prevent division by zero and handle edge cases
      if (
        baselineAvg === 0 ||
        recentAvg === 0 ||
        !isFinite(baselineAvg) ||
        !isFinite(recentAvg)
      ) {
        return { status: "stable", change: 0 };
      }

      const percentageChange = ((recentAvg - baselineAvg) / baselineAvg) * 100;

      // Additional check for non-finite results (Infinity, -Infinity, NaN)
      if (!isFinite(percentageChange)) {
        return { status: "stable", change: 0 };
      }

      if (Math.abs(percentageChange) < 2) {
        return { status: "stable", change: Math.abs(percentageChange) };
      }

      // Interpret trend based on metric type (lower vs higher is better)
      if (lowerIsBetter) {
        // For sprint times and fitness tests: lower values = better performance
        return {
          status: percentageChange < 0 ? "improving" : "declining",
          change: Math.abs(percentageChange),
        };
      } else {
        // For jumps, strength, etc: higher values = better performance
        return {
          status: percentageChange > 0 ? "improving" : "declining",
          change: Math.abs(percentageChange),
        };
      }
    },

    /**
     * Generate insight message based on trend analysis
     */
    generateInsightMessage(metricName, status, change) {
      const changeText = `${change.toFixed(1)}%`;

      if (status === "improving") {
        return `Your ${metricName} has improved by ${changeText} over recent sessions!`;
      } else if (status === "declining") {
        return `Your ${metricName} has declined by ${changeText}. Focus on technique and recovery.`;
      }

      return `Your ${metricName} has remained stable over recent sessions.`;
    },
    populatePysicalPerformanceLineChartLabels() {
      return this.allPlayerPhysicalPerformanceData.map((data) => data.date);
    },
    populatePysicalPerformanceLineChartDataSetsData(dataName) {
      return this.allPlayerPhysicalPerformanceData.map(
        (data) => data[dataName],
      );
    },
    populatePysicalPerformanceLineChartDataSets() {
      return [
        {
          label: "10m SPRINT",
          backgroundColor: "#91afea",
          data: this.populatePysicalPerformanceLineChartDataSetsData(
            "tenM_sprint_time",
          ),
        },
        {
          label: "30m SPRINT",
          backgroundColor: "#c791ea",
          data: this.populatePysicalPerformanceLineChartDataSetsData(
            "fortyM_sprint_time",
          ),
        },
      ];
    },
    populatePhysicalPerformanceLineChartData() {
      this.physicalPerformanceLineChartData = {
        labels: this.populatePysicalPerformanceLineChartLabels(),
        datasets: this.populatePysicalPerformanceLineChartDataSets(),
      };

      this.physicalPerformanceLineCountermovementJumpChartData = {
        labels: this.populatePysicalPerformanceLineChartLabels(),
        datasets: this.populatePysicalPerformanceSingleDataPointChartSets(
          "Countermovement jump",
          "#91afea",
          "cuntermovement_jump",
        ),
      };

      this.physicalPerformanceLineReactiveStrengthIndexChartData = {
        labels: this.populatePysicalPerformanceLineChartLabels(),
        datasets: this.populatePysicalPerformanceSingleDataPointChartSets(
          "Reactive strength index",
          "#91afea",
          "reactive_strength_index",
        ),
      };

      this.physicalPerformanceLineThirtyFithteenIntermittentFitnessTestChartData =
      {
        labels: this.populatePysicalPerformanceLineChartLabels(),
        datasets: this.populatePysicalPerformanceSingleDataPointChartSets(
          "Intermittent Fitness Test",
          "#91afea",
          "thirty_15_intermittent_fitness_test",
        ),
      };

      this.physicalPerformanceLineHeightChartData = {
        labels: this.populatePysicalPerformanceLineChartLabels(),
        datasets: this.populatePysicalPerformanceSingleDataPointChartSets(
          "Height",
          "#91afea",
          "height",
        ),
      };

      this.physicalPerformanceLineWeightChartData = {
        labels: this.populatePysicalPerformanceLineChartLabels(),
        datasets: this.populatePysicalPerformanceSingleDataPointChartSets(
          "Weight",
          "#91afea",
          "weight",
        ),
      };
    },
    populatePysicalPerformanceSingleDataPointChartSets(
      lableName,
      colour,
      typeOfData,
    ) {
      return [
        {
          label: lableName,
          backgroundColor: colour,
          data: this.populatePysicalPerformanceLineChartDataSetsData(
            typeOfData,
          ),
        },
      ];
    },
  },
};
</script>
<style scoped lang="scss">
.physical-performance-wrapper {
  .physical-performance-stats {
    ul {
      li {
        display: flex;
        font-weight: bold;
        margin-bottom: 0.5rem;
        position: relative;
        padding-right: 2.7rem;
        align-items: start;
        border-bottom: 1px solid #a9b3bc;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;

        span {
          font-weight: normal;
          width: 17rem;
        }

        span.time-value {
          width: 6rem !important;
          padding: 0 0.5rem;
        }

        button {
          margin-left: 0.5rem;
          margin-bottom: 0;
        }

        img {
          width: 1.3rem;
          margin: 0.5rem 0.5rem 0 0.5rem;
        }
      }
    }
  }
}

// Compact filter toolbar (Timeline / Metric / date range).
.pp-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem 1rem;
  width: 100%;
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
  background: #f9fafb;
  border: 1px solid #eef0f3;
  border-radius: 10px;
}

.pp-filter {
  margin-bottom: 0;
  flex: 1 1 180px;
  min-width: 160px;
  max-width: 240px;

  :deep(label),
  :deep(legend) {
    font-size: 0.72rem;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 0.3rem;
    padding: 0;
  }
}

.pp-filter-date {
  flex: 1 1 240px;
  max-width: 320px;
}

.physical-performance-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  width: 100%;
}

// Latest-results tiles (replaced the old auto-scrolling session carousel).
.pp-latest-date {
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
  color: #6b7280;
}

.pp-tile-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.75rem;
}

.pp-tile {
  background: #f9fafb;
  border: 1px solid #eef0f3;
  border-radius: 10px;
  padding: 0.75rem 0.9rem;
}

.pp-tile-label {
  font-size: 0.78rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.pp-tile-value {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
}

.pp-tile-unit {
  font-size: 0.85rem;
  font-weight: 600;
  color: #9ca3af;
}

.pp-arrow {
  margin-left: auto;
  font-size: 1rem;

  &.better {
    color: #1a8a4f;
  }

  &.worse {
    color: #c0392b;
  }
}

.pp-empty {
  padding: 1rem 0;
  color: #9ca3af;
}

.pp-actions-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.25rem;
}

.physical-performance-trends {
  overflow-x: scroll;
}

/* Performance Insights Styles */
.performance-insights-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.performance-insights-section h5 {
  margin-bottom: 1rem;
  color: #495057;
  font-weight: 600;
}

.insights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.insight-card {
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  transition: all 0.2s ease;
}

.insight-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.insight-card.improving {
  border-left: 4px solid #28a745;
}

.insight-card.declining {
  border-left: 4px solid #ffc107;
}

.insight-card.stable {
  border-left: 4px solid #6c757d;
}

.insight-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.insight-metric {
  font-weight: 600;
  color: #495057;
}

.insight-trend-icon {
  font-size: 1.2rem;
}

.insight-message {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
}

@media screen and (max-width: 768px) {
  .insights-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .insight-card {
    padding: 0.75rem;
  }

  .performance-insights-section {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }
}

@media screen and (min-width: 1024px) {
  .physical-performance-stats {
    flex: 1;
  }

  .physical-performance-trends {
    flex: 4;
  }
}
</style>
